// src/services/producerService.ts
import SocketService from '@src/services/SocketService';
import {
  useVideoStreamStore,
  useAudioStreamStore,
  useProducersStore,
  useConsumersStore,
  usePeersStore,
  useChatStore,
} from '@src/stores';
import { ECommonSocketKeys } from '@src/types';
import { Transport } from 'mediasoup-client/lib/Transport';

/** Events that are re-registered on reconnection (transport/media only). Connection events stay on. */
const TRANSPORT_SOCKET_EVENTS = [
  ECommonSocketKeys.CONSUMER_CLOSED,
  ECommonSocketKeys.SET_VIDEO_OFF,
  ECommonSocketKeys.REFRESH_PARTICIPANTS_COUNT,
  ECommonSocketKeys.NEW_PRODUCERS,
  ECommonSocketKeys.UPDATE_PEER_INFO,
  ECommonSocketKeys.AUDIO_VOLUME,
] as const;

export interface ICleanProducersOptions {
  /**
   * If false, only remove transport/media listeners so reconnection can keep connect/disconnect
   * registered once. If true (default), remove all listeners (use when leaving the room).
   */
  removeConnectionListeners?: boolean;
}

/**
 * Closes transports, resets stores, and removes socket listeners.
 * Use removeConnectionListeners: false only in the reconnection path so connect/disconnect
 * are never removed and double-join cannot happen.
 */
export const cleanProducers = (consumerTransport?: Transport | null, options: ICleanProducersOptions = {}) => {
  const { removeConnectionListeners } = options;

  // Close transports
  if (consumerTransport) {
    consumerTransport.close();
  }

  const producerTransport = useProducersStore.getState().producerTransport;
  if (producerTransport) {
    producerTransport.close();
  }

  // Reset stores
  useVideoStreamStore.getState().resetVideoStreams();
  useAudioStreamStore.getState().resetAudioStreams();
  useProducersStore.getState().resetStates();
  useConsumersStore.getState().resetStates();
  usePeersStore.getState().resetStates();
  useChatStore.getState().resetStates();

  // Remove transport/media listeners (always, so reconnection re-registers them)
  TRANSPORT_SOCKET_EVENTS.forEach((event) => SocketService.off(event));

  // Remove connection listeners only when fully leaving (exit, unmount, beforeunload)
  if (removeConnectionListeners) {
    SocketService.off(ECommonSocketKeys.CONNECT);
    SocketService.off(ECommonSocketKeys.DISCONNECT);
    SocketService.off(ECommonSocketKeys.REMOVE_ME);
  }
};
